import { useEffect, useState } from 'react';
import {
  AppPageHeader,
  ColorSwatch,
  DeleteIcon,
  EditIcon,
  Group,
  InfiniteTable,
} from '@/components/_common';
import DefaultPlusSquareDropdown from '@/components/_common/DefaultPlusSquareDropdown';
import {
  useInstallmentCancel,
  useInstallmentsList,
} from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import {
  AccountPayableColumnConfig,
  AccountPayableFieldOption,
  AccountPayableSituationColor,
  InstallmentCancelRequest,
  InstallmentList,
  InstallmentSituation,
  InstallmentSituationType,
  ListInstallmentsQuery,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  COLUMNS_WIDTH,
  getTableScrollX,
  tableSorterOrder,
} from '@/utils/table';
import { Button, Divider, Menu, Modal, Space, Statistic, Tooltip } from 'antd';
import { ColumnsType, SorterResult } from 'antd/lib/table/interface';
import moment from 'moment';
import React from 'react';
import { useIntl } from 'react-intl';

import InstallmentsExtraFilters from '@/components/Registry/BillToPay/ReleaseForm/InstallmentsExtraFilters';
import StatusTag from '@/components/_common/StatusTag';
import { useDebounce, useDisclosure } from '@/hooks';
import InstallmentsAdvancedModalFilters from '../InstallmentsAdvancedModalFilters';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import SettledMultipleModal from '../SettledMultipleModal';
import formatterPrice from '@/utils/number/formatterPrice';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import SettledPartiallyModal from '../SettledPartiallyModal';
import SettledSingleModal from '../SettledSingleModal';
import { useFinancialConfigurationProfile } from '@/core/domain/financial/FinancialConfiguration/FinancialConfiguration.hooks';
import { convertColunsAccountPayableToProfile } from '@/core/domain/financial/FinancialConfiguration/FinancialConfiguration.types';
import BankConciliationPopoverDetails from '@/components/_common/BankConciliation/BankConciliationPopoverDetails';

const ListInstallments: React.FC = () => {
  const intl = useIntl();
  const [rowsSelected, setRowsSelected] = useState<InstallmentList[]>([]);

  const [advancedFiltersOpened, advancedFiltersAction] = useDisclosure();
  const [modalSettleMultiple, modalSettleMultipleAction] = useDisclosure();

  const financialConfiguration = useFinancialConfigurationProfile();
  const dataProfile = flatInfiniteData(financialConfiguration.data);

  const [modalSettleSingle, modalSettleSingleAction] = useDisclosure();
  const [modalSettlePartially, modalSettlePartiallyAction] = useDisclosure();

  const [settleInstallmentSingleItem, setSettleInstallmentSingleItem] =
    useState<InstallmentList | null>(null);
  const [settleInstallmentPartiallyItem, setSettleInstallmentPartiallyItem] =
    useState<InstallmentList | null>(null);

  const [queryParams, setQueryParams] = useState<ListInstallmentsQuery>({
    order: 'DESC',
    ...convertParams<ListInstallmentsQuery>({
      search: 'string',
      code: 'string',
      companyId: 'number',
      creditor: 'string',
      description: 'string',
      documentTypeId: 'number',
      dueDate: 'string',
      payDate: 'string',
      emissionDate: 'string',
      situation: 'string',
      installmentValue: 'number',
      orderColumns: 'string',
      bankAccountId: 'number',
      costCenterId: 'number',
      documentNumber: 'string',
      purchasingDocumentNumber: 'string',
      purchaseCode: 'string',
      cnpjCreditor: 'string',
      initialDueDate: 'string',
      finalDueDate: 'string',
      initialPayDate: 'string',
      finalPayDate: 'string',
      initialEmissionDate: 'string',
      finalEmissionDate: 'string',
      order: 'string',
      observation: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useInstallmentsList(queryDebounce);

  const installments = flatInfiniteData(data);
  const cancelMutation = useInstallmentCancel();

  function handleOrderAccountPayable(sorter: SorterResult<InstallmentList>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : tableSorterOrder.descend;

          const orderColumns = sorter.order
            ? AccountPayableColumnConfig[sorter.columnKey]
            : undefined;

          if (prev.orderColumns === orderColumns) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order,
            orderColumns,
          };
        }

        return prev;
      });
    }
  }

  const columns: ColumnsType<InstallmentList> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'releaseBillToPay.list.header.code' }),
      width: 140,
      key: AccountPayableFieldOption.code,
      sorter: true,
      render: (_, row) => (
        <Group gap={10}>
          <>{row.code}</>

          {!!row.conciliateInstallmentResponse && (
            <BankConciliationPopoverDetails
              conciliatedInstallment={row.conciliateInstallmentResponse}
            />
          )}
        </Group>
      ),
    },
    {
      dataIndex: 'installmentTag',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.installmentTag',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.installmentTag,
      align: 'center',
      render: (_, row) =>
        !!row.installmentTag && (
          <Group position="center">
            <Tooltip
              title={row.installmentTag.name.toUpperCase()}
              color={row.installmentTag.color}
              placement="right"
            >
              <div>
                <ColorSwatch backgroundColor={row.installmentTag.color} />
              </div>
            </Tooltip>
          </Group>
        ),
    },
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.companies',
      }),
      width: 220,
      key: AccountPayableFieldOption.company,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentNumber',
      }),
      width: 220,
      key: AccountPayableFieldOption.documentNumber,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'creditor',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.creditor',
      }),
      width: 220,
      key: AccountPayableFieldOption.creditor,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.description',
      }),
      width: COLUMNS_WIDTH.action2,
      key: AccountPayableFieldOption.description,
      sorter: true,
    },
    {
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentType',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.documentType,
      sorter: true,
    },
    {
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.emissionDate,
      sorter: true,
    },
    {
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.dueDate,
      sorter: true,
    },
    {
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.payDate,
      sorter: true,
    },
    {
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.situation',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.situation,
      render: (situation: InstallmentSituation) => (
        <StatusTag
          label={InstallmentSituationType[situation]}
          color={AccountPayableSituationColor[situation]}
        />
      ),
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.value',
      }),
      render: (_, record) =>
        `R$ ${quantityFormatter(
          record.installmentValue +
            (record.discountIncrease ?? 0) -
            (record.credit ?? 0),
        )}`,
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.value,
      sorter: true,
    },
    {
      title: intl.formatMessage({ id: 'releaseBillToPay.list.header.actions' }),
      width: 150,
      align: 'right',
      fixed: 'right',
      key: 'actions',
      render: (row: InstallmentList) => (
        <Space>
          <DefaultPlusSquareDropdown
            disabled={
              row.situation === InstallmentSituation.settled ||
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_PAYABLE_INSTALLMENTS_SETTLEMENT,
              ])
            }
          >
            <Menu>
              <Menu.Item
                key="settleSingleInstallment"
                onClick={() => {
                  setSettleInstallmentSingleItem(row);
                  modalSettleSingleAction.open();
                }}
              >
                Liquidar
              </Menu.Item>

              <Menu.Item
                key="settlePartiallyInstallmet"
                onClick={() => {
                  setSettleInstallmentPartiallyItem(row);
                  modalSettlePartiallyAction.open();
                }}
              >
                Liquidar Parcialmente
              </Menu.Item>
            </Menu>
          </DefaultPlusSquareDropdown>

          <Divider type="vertical" />

          <EditIcon
            redirect={`/app/financial/billToPay/releaseBillToPay/detail/accountPayableId/${row.accountPayableId}/installmentPayableId/${row.id}`}
            disabled={
              !hasPermissions([Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={
              row.situation === InstallmentSituation.settled ||
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_PAYABLE_INSTALLMENTS_DELETE,
              ])
            }
            onClick={() => handleRemoveClick(row)}
          />
        </Space>
      ),
    },
  ];

  const activeColumns = columns.filter((item) =>
    dataProfile.some(
      (profile) =>
        (profile.active &&
          item.key &&
          convertColunsAccountPayableToProfile[
            item.key as AccountPayableFieldOption
          ] === profile.column) ||
        item.key === 'actions',
    ),
  );

  const handleRemoveClick = (installment: InstallmentList) => {
    Modal.confirm({
      title: 'Confirmação',
      content: installment.isOriginalCommission
        ? 'Esta conta a pagar está vinculada à comissão. Ao excluir a última parcela, o valor será estornado automaticamente. Deseja continuar com a exclusão?'
        : 'O processo de exclusão de parcelas é irreversível. Deseja realmente excluir a parcela?',
      onOk: async () => {
        const obj: InstallmentCancelRequest = {
          id: installment.accountPayableId,
          installmentPayableId: installment.id,
        };
        await cancelMutation.mutateAsync(obj);
      },
      okType: 'danger',
    });
  };

  return (
    <>
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ order: 'DESC' })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <InstallmentsExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
        handleClickAdvancedFilters={advancedFiltersAction.open}
      />

      <InfiniteTable<InstallmentList>
        rowSelection={{
          preserveSelectedRowKeys: true,
          onChange: (_, rows) => setRowsSelected(rows),
          getCheckboxProps: (item) => ({
            disabled: item.situation === InstallmentSituation.settled,
          }),
          selectedRowKeys: rowsSelected.map((item) => item.id),
        }}
        columns={activeColumns}
        dataSource={installments}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrderAccountPayable(sorter);
          }
        }}
        title={() => (
          <SpaceForButton
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Statistic
              title="Total de parcelas selecionadas"
              value={rowsSelected.reduce(
                (acc, curr) =>
                  (acc +=
                    curr?.installmentValue +
                    curr?.discountIncrease -
                    curr?.credit),
                0,
              )}
              formatter={(value) => formatterPrice(Number(value))}
              valueStyle={{ color: 'rgb(160, 206, 51)' }}
            />

            <Button
              block
              onClick={modalSettleMultipleAction.open}
              type="primary"
              disabled={
                (rowsSelected?.length || 0) < 1 ||
                !hasPermissions([
                  Authorities.FINANCIAL_ACCOUNT_PAYABLE_INSTALLMENTS_SETTLEMENT,
                ])
              }
            >
              {intl.formatMessage({
                id: 'releaseBillToPay.searchBar.settleMultipleInstallments',
              })}
            </Button>
          </SpaceForButton>
        )}
      />

      <InstallmentsAdvancedModalFilters
        open={advancedFiltersOpened}
        onCancel={advancedFiltersAction.close}
        handleSetFilter={(values) =>
          setQueryParams((prev) => ({ ...prev, ...values }))
        }
        queryParams={queryParams}
      />

      <SettledSingleModal
        open={modalSettleSingle}
        onCancel={() => {
          setSettleInstallmentSingleItem(null);
          modalSettleSingleAction.close();
        }}
        data={{
          id: settleInstallmentSingleItem?.accountPayableId,
          installmentPayableId: settleInstallmentSingleItem?.id,
          installmentValue: settleInstallmentSingleItem?.installmentValue,
          bankAccountId: settleInstallmentSingleItem?.bankAccountId,
          credit: settleInstallmentSingleItem?.credit,
          currentSituation: settleInstallmentSingleItem?.situation,
          discountIncrease: settleInstallmentSingleItem?.discountIncrease,
          documentTypeId: settleInstallmentSingleItem?.documentTypeId,
        }}
      />

      <SettledPartiallyModal
        open={modalSettlePartially}
        onCancel={() => {
          setSettleInstallmentPartiallyItem(null);
          modalSettlePartiallyAction.close();
        }}
        data={{
          id: settleInstallmentPartiallyItem?.accountPayableId,
          installmentPayableId: settleInstallmentPartiallyItem?.id,
          installmentValue: settleInstallmentPartiallyItem?.installmentValue,
          bankAccountId: settleInstallmentPartiallyItem?.bankAccountId,
          credit: settleInstallmentPartiallyItem?.credit,
          currentSituation: settleInstallmentPartiallyItem?.situation,
          discountIncrease: settleInstallmentPartiallyItem?.discountIncrease,
          documentTypeId: settleInstallmentPartiallyItem?.documentTypeId,
        }}
      />

      <SettledMultipleModal
        open={modalSettleMultiple}
        onCancel={modalSettleMultipleAction.close}
        installmentsSelected={rowsSelected}
        onOk={() => {
          modalSettleMultipleAction.close();
          setRowsSelected([]);
        }}
      />
    </>
  );
};

export default ListInstallments;
